/**
 * Action Center card logic.
 *
 * Pure, component-free helpers for the role-scoped cards the Action Center
 * surfaces (currently the Failed Payments follow-up). Kept decoupled from React
 * and the API layer so each card's rules stay unit-testable in isolation; add
 * further cards' logic here as they are introduced.
 */
import {
  isProjectEmailTemplateConfigured,
  type ParticipantEmailTemplateType,
  type ProjectEmailTemplateConfig,
} from '@/shared/services/api-client';
import {
  PAYMENT_REQUEST_SOURCE_LABEL,
  isPartialPaymentRequest,
  resolvePaymentRequestSource,
  type PaymentRequestSourceRow,
} from '@/shared/lib/payment-requests';
import type {RoleKey} from './action-center-roles';

/**
 * Whether the Failed Payments badge should surface for the active tab.
 *
 * It is a PM-only follow-up: the PM tab is the one place that tracks payments
 * owed across the project, and there is nothing to act on until OneForma has
 * actually rejected a payment — so the badge stays hidden at a zero count and
 * the empty state shows instead.
 */
export const shouldShowFailedPaymentsBadge = (
  role: RoleKey | null,
  failedCount: number
): boolean => role === 'pm' && failedCount > 0;

/**
 * Deep link to Payment Approvals with the Failed quick-filter pre-activated.
 * Reuses the existing `payment-approvals` tab route and the approvals page's
 * Failed pill rather than introducing a second filter surface.
 */
export const buildFailedPaymentsHref = (projectId: string): string =>
  `/projects?projectId=${encodeURIComponent(
    projectId
  )}&tab=payment-approvals&paymentFilter=failed`;

/**
 * The participant email templates the Email Setup screen tracks. Kept in sync
 * with that screen's tab list so the Action Center reports the same "N of
 * total templates configured" status rather than deriving a second,
 * divergent one.
 */
export const EMAIL_SETUP_TEMPLATE_TYPES: ParticipantEmailTemplateType[] = [
  'booking_confirmation',
  'booking_confirmation_self_booking',
  'reschedule_confirmation',
  'cancellation_confirmation',
  'moderator_change',
  'session_completed',
  'reminder',
  'no_show',
  'self_booking_invite',
  'self_booking_reminder',
];

/** Total configurable templates — the denominator in the Email Setup status line. */
export const EMAIL_SETUP_TEMPLATE_TOTAL = EMAIL_SETUP_TEMPLATE_TYPES.length;

/**
 * How many templates are configured, reusing the same
 * subject-and-body check the Email Setup screen applies. Accepts just the
 * project's stored config so callers don't need a full Project.
 */
export const countConfiguredEmailTemplates = (
  emailTemplateConfig: ProjectEmailTemplateConfig | null | undefined
): number =>
  EMAIL_SETUP_TEMPLATE_TYPES.filter((type) =>
    isProjectEmailTemplateConfigured({email_template_config: emailTemplateConfig}, type)
  ).length;

/**
 * Whether the Email Setup follow-up card should surface for the active tab.
 *
 * Like Failed Payments it is PM-only, and there is nothing to act on once all
 * templates are configured — so the card hides at a full count and the
 * empty state shows instead.
 */
export const shouldShowEmailSetupCard = (
  role: RoleKey | null,
  configuredCount: number
): boolean => role === 'pm' && configuredCount < EMAIL_SETUP_TEMPLATE_TOTAL;

/** The "N of total templates" status line reused from the Email Setup screen. */
export const formatEmailSetupStatus = (configuredCount: number): string =>
  `${configuredCount} of ${EMAIL_SETUP_TEMPLATE_TOTAL} templates`;

/** Deep link to the project's Email Setup screen, where the templates are configured. */
export const buildEmailSetupHref = (projectId: string): string =>
  `/projects?projectId=${encodeURIComponent(projectId)}&tab=email-setup`;

/**
 * Whether the Pending Payment Requests badge should surface for the active
 * tab. PM-only, like Failed Payments: a Moderator/Scheduler submitting
 * "Request Payment Approval" (with or without a bonus/partial-payment flag)
 * has nothing further to act on, so only the PM sees this. `pendingCount`
 * comes straight from the same `/payment-stats` aggregate the Payment
 * Approvals page's own "Pending Payments" KPI card reads, so the two numbers
 * can never drift apart.
 */
export const shouldShowPendingPaymentRequestsBadge = (
  role: RoleKey | null,
  pendingCount: number
): boolean => role === 'pm' && pendingCount > 0;

/**
 * Deep link to Payment Approvals with the Pending quick-filter pre-activated
 * — the same tile a PM could click on that page themselves.
 */
export const buildPendingPaymentRequestsHref = (projectId: string): string =>
  `/projects?projectId=${encodeURIComponent(
    projectId
  )}&tab=payment-approvals&paymentFilter=pending`;

/**
 * Whether the "no primary moderator" follow-up card should surface for the
 * active tab. Unlike the PM-only cards above this is shared by the PM and
 * Scheduler tabs — both are responsible for getting a primary moderator onto
 * every session — and there is nothing to act on until at least one session is
 * missing its primary moderator, so the card stays hidden at a zero count and
 * the empty state shows instead.
 */
export const shouldShowNoPrimaryModeratorCard = (
  role: RoleKey | null,
  missingCount: number
): boolean => (role === 'pm' || role === 'scheduler') && missingCount > 0;

/**
 * Deep link to the Scheduler (the assignment screen) with its existing
 * "Unassigned" moderator quick-filter pre-activated, so the PM lands on exactly
 * the sessions that still need a primary moderator assigned.
 */
export const buildNoPrimaryModeratorHref = (projectId: string): string =>
  `/projects?projectId=${encodeURIComponent(
    projectId
  )}&tab=scheduler&moderatorFilter=unassigned`;

/**
 * Whether the "Upcoming sessions" card should surface for the active tab. It is
 * a Moderator-only card scoped to the viewer's own booked sessions, and there
 * is nothing to act on until at least one session is still upcoming, so the
 * card stays hidden at a zero count and the empty state shows instead.
 */
export const shouldShowUpcomingSessionsCard = (
  role: RoleKey | null,
  upcomingCount: number
): boolean => role === 'moderator' && upcomingCount > 0;

/**
 * Whether the "Cancelled sessions" card should surface. Moderator-only and
 * self-scoped like the upcoming card; hidden until the viewer has at least one
 * cancelled session.
 */
export const shouldShowCancelledSessionsCard = (
  role: RoleKey | null,
  cancelledCount: number
): boolean => role === 'moderator' && cancelledCount > 0;

/**
 * Whether the "No-show follow-up needed" card should surface for the active
 * tab. Like the upcoming card it is Moderator-only and self-scoped, and stays
 * hidden until at least one of the moderator's no-show sessions is still missing
 * its follow-up note.
 */
export const shouldShowNoShowFollowUpCard = (
  role: RoleKey | null,
  noShowCount: number
): boolean => role === 'moderator' && noShowCount > 0;

/**
 * Deep link to the moderator's Booked Sessions view, where they can see their
 * upcoming sessions and add the follow-up note to a no-show. Both Moderator
 * cards route here — the relevant session/schedule detail for either action.
 */
export const buildModeratorScheduleHref = (projectId: string): string =>
  `/schedules?projectId=${encodeURIComponent(projectId)}&tab=booked-sessions`;

/**
 * One add/remove event for the current moderator on an upcoming session, as
 * returned by GET /moderators/project/:projectId/my-session-changes. Self-scoped
 * by the backend to the viewer, so this is how a removed moderator (who no
 * longer appears on the session) learns they were unassigned, and an added
 * moderator sees their assignment.
 */
export interface MySessionChange {
  id: string;
  action: 'added' | 'removed';
  role: 'primary' | 'secondary';
  scheduleId: string;
  sessionTitle: string | null;
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  meetingLink: string | null;
  status: string | null;
  projectName: string | null;
  createdAt: string | null;
}

/** A session-change row prepared for display in the Action Center card. */
export interface MySessionChangeItem {
  id: string;
  action: 'added' | 'removed';
  /** 'Primary Moderator' / 'Secondary/Backup Moderator' — matches the email wording. */
  roleLabel: string;
  sessionTitle: string;
  scheduleId: string;
  startTime: string | null;
  location: string | null;
  projectName: string | null;
}

/** The moderator role, spelled the same way the add/remove emails spell it. */
export const formatModeratorRoleLabel = (role: string): string =>
  role === 'secondary' ? 'Secondary/Backup Moderator' : 'Primary Moderator';

/**
 * Whether the "Session assignment updates" card should surface. Moderator-only
 * and self-scoped like the other moderator cards; hidden at a zero count so the
 * empty state shows instead.
 */
export const shouldShowMySessionChangesCard = (
  role: RoleKey | null,
  changeCount: number
): boolean => role === 'moderator' && changeCount > 0;

/** Map raw endpoint rows to render-ready items. */
export const buildMySessionChangeItems = (
  rows: readonly MySessionChange[]
): MySessionChangeItem[] =>
  rows.map((row) => ({
    id: String(row.id),
    action: row.action === 'removed' ? 'removed' : 'added',
    roleLabel: formatModeratorRoleLabel(row.role),
    sessionTitle: String(row.sessionTitle || 'Session'),
    scheduleId: String(row.scheduleId),
    startTime: row.startTime ?? null,
    location: row.location ?? null,
    projectName: row.projectName ?? null,
  }));

/**
 * Deep link the moderator to their Booked Sessions view — where they can see
 * the session they were added to (and the ones they were removed from drop off).
 */
export const buildMySessionChangesHref = (projectId: string): string =>
  `/schedules?projectId=${encodeURIComponent(projectId)}&tab=booked-sessions`;

/**
 * Deep link a PM to one moderator's *upcoming* Booked Sessions: the same Booked
 * Sessions view the moderator self-cards use, but with the moderator
 * pre-filtered (by user id) and the upcoming/past toggle left on "upcoming", so
 * the PM lands on exactly that moderator's next sessions.
 */
export const buildModeratorUpcomingHref = (
  projectId: string,
  moderatorId: string
): string =>
  `/schedules?projectId=${encodeURIComponent(
    projectId
  )}&tab=booked-sessions&moderatorId=${encodeURIComponent(
    moderatorId
  )}&sessionTime=upcoming`;

/**
 * Deep link a PM to one moderator's no-show Booked Sessions: the Booked Sessions
 * view pre-filtered to that moderator (by user id) with the No-show status
 * quick-filter applied and the toggle on "past", where completed no-shows live.
 */
export const buildModeratorNoShowHref = (
  projectId: string,
  moderatorId: string
): string =>
  `/schedules?projectId=${encodeURIComponent(
    projectId
  )}&tab=booked-sessions&moderatorId=${encodeURIComponent(
    moderatorId
  )}&status=no_show&sessionTime=past`;

/**
 * Deep link a PM to one moderator's cancelled Booked Sessions: the Cancelled
 * tab, pre-filtered to that moderator, where every cancelled session lives
 * regardless of original date.
 */
export const buildModeratorCancelledHref = (
  projectId: string,
  moderatorId: string
): string =>
  `/schedules?projectId=${encodeURIComponent(
    projectId
  )}&tab=booked-sessions&moderatorId=${encodeURIComponent(
    moderatorId
  )}&sessionTime=cancelled`;

/** Deep link the viewing moderator to their own Cancelled Booked Sessions tab. */
export const buildCancelledSessionsHref = (projectId: string): string =>
  `/schedules?projectId=${encodeURIComponent(projectId)}&tab=booked-sessions&sessionTime=cancelled`;

/** Counts backing the Moderator-tab cards, plus the viewer/tab context that gates them. */
export interface ModeratorCardVisibilityInput {
  role: RoleKey | null;
  hasProject: boolean;
  viewerIsPm: boolean;
  /**
   * OD-1342: a scheduler reads the Moderator tab the way a PM does — the whole
   * project's moderators, not their own sessions. Separate from `viewerIsPm`
   * because the two roles still differ everywhere else on this screen.
   */
  viewerIsScheduler?: boolean;
  upcomingCount: number;
  cancelledCount: number;
  noShowFollowUpCount: number;
  mySessionChangeCount: number;
  moderatorSummaryCount: number;
  /**
   * OD-1343: items in the ownerless group. Counted here because a project whose
   * ONLY pending work is unassigned has no named moderator to make the grouping
   * visible — and that is exactly the blind spot the group exists to close.
   */
  unassignedCount?: number;
}

/**
 * Which Moderator-tab cards to render. The self cards only surface for an
 * actual moderator; a PM on that tab gets the per-moderator rollup instead, one
 * section per moderator, rendered only when at least one has a pending action.
 */
export const resolveModeratorCardVisibility = ({
  role,
  hasProject,
  viewerIsPm,
  viewerIsScheduler = false,
  upcomingCount,
  cancelledCount,
  noShowFollowUpCount,
  mySessionChangeCount,
  moderatorSummaryCount,
  unassignedCount = 0,
}: ModeratorCardVisibilityInput): {
  showUpcomingSessions: boolean;
  showCancelledSessions: boolean;
  showNoShowFollowUp: boolean;
  showMySessionChanges: boolean;
  showModeratorSummaries: boolean;
} => {
  // Who reads this tab as an overview of the project rather than as their own
  // worklist. A scheduler is here to find freed slots across every moderator,
  // so showing them their own (empty) moderator cards would be noise.
  const seesEveryModerator = viewerIsPm || viewerIsScheduler;
  const selfCardsVisible = hasProject && !seesEveryModerator;
  return {
    showUpcomingSessions:
      selfCardsVisible && shouldShowUpcomingSessionsCard(role, upcomingCount),
    showCancelledSessions:
      selfCardsVisible && shouldShowCancelledSessionsCard(role, cancelledCount),
    showNoShowFollowUp:
      selfCardsVisible &&
      shouldShowNoShowFollowUpCard(role, noShowFollowUpCount),
    showMySessionChanges:
      selfCardsVisible &&
      shouldShowMySessionChangesCard(role, mySessionChangeCount),
    showModeratorSummaries:
      role === 'moderator' &&
      seesEveryModerator &&
      hasProject &&
      (moderatorSummaryCount > 0 || unassignedCount > 0),
  };
};

/**
 * Whether a payment-request follow-up card (Bonus Requests, Partial Payment
 * Requests) should surface for the active tab (OD-1070). They are PM-only
 * follow-ups: a request is raised by a Moderator/Scheduler flag or by a
 * with-payment cancellation/reschedule, but only a PM approves it, on Payment
 * Approvals — so a card stays hidden at a zero count and the empty state shows
 * instead.
 *
 * Both the active tab AND the viewer's own role are checked, the same
 * belt-and-braces the Moderator self-cards apply: the PM tab is only offered
 * to a PM today, but a non-PM must never see other people's payment work even
 * if that ever changes.
 */
export const shouldShowPaymentRequestCard = (
  role: RoleKey | null,
  requestCount: number,
  viewerIsProjectManager: boolean
): boolean => role === 'pm' && viewerIsProjectManager && requestCount > 0;

/** The subset of a flagged session row this module reads. */
type BonusRequestRow = {payment_status?: unknown};

/**
 * The flagged rows still awaiting a PM decision. Approved/rejected rows come
 * back in the same fetch (they feed the card's per-moderator/participant
 * running totals) but are not open work, so only these are listed and counted.
 */
export const filterPendingBonusRequests = <T extends BonusRequestRow>(
  rows: readonly T[]
): T[] =>
  rows.filter(
    (row) => String(row.payment_status ?? '').toLowerCase() === 'pending'
  );

/**
 * Deep link to Payment Approvals with one queue's filter pre-activated
 * (OD-1070) — reuses the existing Payment Approvals screen rather than hosting
 * an embedded approve/amount-entry UI in Action Center. Each card links to its
 * own queue, so a PM who opened Bonus Requests never lands on partial payments.
 *
 * `participantId`, when the row it came from has one, scopes the destination to
 * that one participant's requests — otherwise a PM clicking a specific flagged
 * row would land on every pending request in the queue project-wide, which
 * reads as "wrong records" when more than one is open at once.
 */
export const buildPaymentRequestHref = (
  projectId: string,
  kind: 'bonus' | 'partial_payment',
  participantId?: string
): string =>
  `/projects?projectId=${encodeURIComponent(
    projectId
  )}&tab=payment-approvals&paymentRequest=${kind}` +
  (participantId
    ? `&participantId=${encodeURIComponent(participantId)}`
    : '');

/** The subset of a project team member this module reads. */
type TeamRoleRow = {member_id?: unknown; role?: unknown};

/**
 * Display label for a `team.role` value: 'moderator' → 'Moderator',
 * 'project_manager' → 'Project Manager'.
 *
 * Returns null for a blank/absent role so callers can leave the parenthetical
 * off entirely rather than render a meaningless "(Unknown)".
 */
export const formatTeamRoleLabel = (role: unknown): string | null => {
  const raw = String(role ?? '').trim();
  if (!raw) return null;
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * userId → role label for the project's team, so a flagged session can be shown
 * as "Flagged by <name> (<Role>)". Built once per roster rather than rescanned
 * for every row. The flagger is keyed on `member_id` because that is the
 * auth user id a flag's `bonus_flagged_by` records.
 *
 * The first membership wins: the roster is one row per member per project, and
 * preferring the earlier row keeps the label stable if that ever stops holding.
 */
export const buildTeamRoleLabelsByUserId = (
  members: readonly TeamRoleRow[]
): Map<string, string> => {
  const labels = new Map<string, string>();
  for (const member of members) {
    const userId = String(member.member_id ?? '').trim();
    const label = formatTeamRoleLabel(member.role);
    if (userId && label && !labels.has(userId)) labels.set(userId, label);
  }
  return labels;
};

/**
 * Whether the "No-show / cancellation limit reached" follow-up card should
 * surface for the active tab (OD-1102). PM-only: the card expands in place so
 * the PM can allow or block each flagged participant right there — nothing
 * is auto-blocked, and there's nothing to act on until at least one
 * participant has reached their session type's configured limit and has no
 * decision recorded yet, or has been blocked, so the card stays hidden at a
 * zero count and the empty state shows instead.
 */
export const shouldShowNoShowLimitCard = (
  role: RoleKey | null,
  atLimitCount: number
): boolean => role === 'pm' && atLimitCount > 0;

// --- Payment requests: Bonus vs Partial Payment ----------------------------

/** The subset of a payment-request row the cards read. */
export interface PaymentRequestRow
  extends BonusRequestRow,
    PaymentRequestSourceRow {
  id?: unknown;
  title?: unknown;
  payment_amount?: unknown;
  participant_id?: unknown;
  participant_first_name?: unknown;
  participant_last_name?: unknown;
  bonus_flag_types?: unknown;
  bonus_flag_note?: unknown;
  bonus_flagged_by?: unknown;
  bonus_flagged_by_name?: unknown;
}

/** The rows belonging to one card: bonus flags, or partial payments. */
export const filterPaymentRequests = <T extends PaymentRequestRow>(
  rows: readonly T[],
  partialPayments: boolean
): T[] => rows.filter((row) => isPartialPaymentRequest(row) === partialPayments);

/** Map keys are stringified so a row with no id can't collide with a real one. */
const idKey = (value: unknown): string => String(value ?? '');

/**
 * How many requests of this kind each flagger has raised on the project —
 * approved and rejected ones included, so the PM sees the person's full volume
 * rather than only what is still open.
 */
export const countRequestsByFlagger = (
  rows: readonly PaymentRequestRow[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.bonus_flagged_by) continue;
    const key = idKey(row.bonus_flagged_by);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
};

/** How much of this kind each participant has already been approved for. */
export const countApprovedByParticipant = (
  rows: readonly PaymentRequestRow[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (String(row.payment_status ?? '').toLowerCase() !== 'approved') continue;
    if (!row.participant_id) continue;
    const key = idKey(row.participant_id);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
};

/** One row of a payment-request card, ready to render. */
export interface PaymentRequestItem {
  id: string;
  title: string;
  types: string[];
  /** 'Cancellation' / 'Reschedule'; null on a bonus request. */
  sourceLabel: string | null;
  flaggedByName: string;
  flaggerRoleLabel: string | null;
  requestCount: number;
  participantId: string;
  participantName: string;
  receivedCount: number;
  note: string | null;
  /** Null when the request is worth nothing knowable yet, so the line is dropped. */
  amount: number | null;
}

/** What a row needs from the project around it to be rendered. */
export interface PaymentRequestItemContext {
  /** Every selected type's configured amount, summed; null when unpriced. */
  configuredAmount: (types: string[]) => number | null;
  roleLabelByFlagger: Map<string, string>;
  requestCountByFlagger: Map<string, number>;
  approvedCountByParticipant: Map<string, number>;
}

const requestTypes = (row: PaymentRequestRow): string[] =>
  Array.isArray(row.bonus_flag_types) ? row.bonus_flag_types : [];

const participantName = (row: PaymentRequestRow): string =>
  [row.participant_first_name, row.participant_last_name]
    .filter(Boolean)
    .join(' ') || idKey(row.participant_id);

/**
 * What the request is worth: the amount it was raised at once one is recorded —
 * a partial payment is priced from the project's configuration the moment it is
 * raised — falling back to the configured total of its types, which is all a
 * bonus flag carries until the PM prices it.
 */
const requestAmount = (
  row: PaymentRequestRow,
  configuredTotal: number | null
): number | null => {
  const raised = Number(row.payment_amount) || 0;
  return raised > 0 ? raised : configuredTotal;
};

/** Rows as the card renders them, with every per-row lookup already resolved. */
export const buildPaymentRequestItems = (
  rows: readonly PaymentRequestRow[],
  context: PaymentRequestItemContext
): PaymentRequestItem[] =>
  rows.map((row) => {
    const types = requestTypes(row);
    return {
      id: idKey(row.id),
      title: String(row.title || `Session ${idKey(row.id).slice(0, 8)}`),
      types,
      sourceLabel: PAYMENT_REQUEST_SOURCE_LABEL[resolvePaymentRequestSource(row)],
      flaggedByName: String(row.bonus_flagged_by_name || 'Unknown'),
      flaggerRoleLabel:
        context.roleLabelByFlagger.get(idKey(row.bonus_flagged_by)) ?? null,
      requestCount:
        context.requestCountByFlagger.get(idKey(row.bonus_flagged_by)) ?? 1,
      participantId: idKey(row.participant_id),
      participantName: participantName(row),
      receivedCount:
        context.approvedCountByParticipant.get(idKey(row.participant_id)) ?? 0,
      note: row.bonus_flag_note ? String(row.bonus_flag_note) : null,
      amount: requestAmount(row, context.configuredAmount(types)),
    };
  });

/**
 * Moderator availability rows awaiting a PM decision (OD-1259). A row is
 * pending when it is still `pending_approval` (add/update) or when an approved
 * slot is flagged `pending_action = delete` until the PM confirms.
 */
export type AvailabilityApprovalRow = {
  moderator_id?: unknown;
  status?: unknown;
  pending_action?: unknown;
};

const PENDING_AVAILABILITY_ACTIONS = new Set(['add', 'update', 'delete']);

export const isPendingAvailabilityApproval = (
  row: AvailabilityApprovalRow
): boolean => {
  const status = String(row.status ?? '').toLowerCase();
  const action = String(row.pending_action ?? '').toLowerCase();
  return status === 'pending_approval' || PENDING_AVAILABILITY_ACTIONS.has(action);
};

export const filterPendingAvailabilityApprovals = <
  T extends AvailabilityApprovalRow,
>(
  rows: readonly T[]
): T[] => rows.filter(isPendingAvailabilityApproval);

/**
 * Distinct moderators with at least one pending availability change. Counted
 * per person, not per slot — one weekly save must not inflate the badge.
 */
export const countPendingAvailabilityApprovals = (
  rows: readonly AvailabilityApprovalRow[]
): number => {
  const ids = new Set<string>();
  for (const row of filterPendingAvailabilityApprovals(rows)) {
    const id = String(row.moderator_id ?? '').trim();
    if (id) ids.add(id);
  }
  return ids.size;
};

export const shouldShowAvailabilityApprovalsCard = (
  role: RoleKey | null,
  pendingCount: number,
  viewerIsProjectManager: boolean
): boolean => role === 'pm' && viewerIsProjectManager && pendingCount > 0;

/** Deep link to Schedule → Moderator's Availability, where the PM approves. */
export const buildAvailabilityApprovalsHref = (projectId: string): string =>
  `/schedules?projectId=${encodeURIComponent(projectId)}&tab=moderators-availability`;

/* ------------------------------------------------------------------------ *
 * Data Uploads — failed and partially complete uploads (OD-1332 AC-3.x)
 * ------------------------------------------------------------------------ */

export interface UploadActionItem {
  id: string;
  batch_ref: string;
  project_id: string;
  status: 'failed' | 'partial';
  identifier: string | null;
  total_files: number;
  succeeded_files: number;
  failed_files: number;
  total_bytes?: number | string | null;
  uploaded_bytes?: number | string | null;
  files: Array<{
    name: string;
    status: string;
    reason?: string | null;
    progress?: number | null;
    size?: number | null;
    booking_group_id?: string | null;
  }>;
  started_at: string;
  ended_at: string | null;
  started_by_name: string | null;
  started_by?: string | null;
  /** Action Center tab this item belongs on. Null stays on the PM tab only. */
  started_by_role?: 'pm' | 'scheduler' | 'moderator' | null;
}

/**
 * A successful upload never reaches the Action Center (AC-3.1) — the uploader
 * comes back to OneData for the next step anyway, so the panel is enough. Only
 * an outcome that needs somebody to do something appears here.
 */
export const shouldShowUploadIssuesCard = (items: UploadActionItem[]): boolean =>
  items.length > 0;

/**
 * What the item says: what failed, why, what was retained, who started it and
 * when (AC-3.3).
 *
 * The retained line is not optional. After a two-hour upload fails on the last
 * file, the only question that matters is whether to re-upload one file or all
 * four, and an item that does not answer it sends someone to the page to work
 * it out by hand.
 */
const failedUploadFiles = (item: UploadActionItem) =>
  (item.files || []).filter((file) => file.status === 'failed');

const fileCount = (item: UploadActionItem, fallback: number): number =>
  item.total_files || (item.files || []).length || fallback;

/** "164.9 GB". Gigabytes keep one decimal, matching the Action Center reference. */
export const formatUploadSize = (bytes: number | string | null | undefined): string | null => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** exponent;
  const shown =
    exponent >= 3
      ? scaled.toFixed(1)
      : scaled >= 10 || exponent === 0
        ? String(Math.round(scaled))
        : scaled.toFixed(1);
  return `${shown} ${units[exponent]}`;
};

const formatClock = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(date);
};

/** A rejected name is the amber card. A dropped transfer is the red card, even when other files were kept. */
const REJECTION_REASON = /already exists|status code 409|\b409\b|duplicate/i;
const NAME_ALREADY_EXISTS = 'a file of that name already exists in this folder';

/** The mock wording. A 409 from the server is this same case. */
export const rejectionCopy = (reason?: string | null): string => {
  const text = String(reason || '').trim();
  if (!text) return '';
  return REJECTION_REASON.test(text) ? NAME_ALREADY_EXISTS : text;
};

export const uploadWasRejected = (item: UploadActionItem): boolean =>
  REJECTION_REASON.test(String(failedUploadFiles(item)[0]?.reason || ''));

/** Red "Upload failed" card, including "1 of 4 failed, 3 retained". */
export const uploadPresentsAsFailure = (item: UploadActionItem): boolean =>
  !uploadWasRejected(item);

const retainedSentence = (kept: number): string =>
  kept > 0
    ? ` The ${kept} completed file${kept === 1 ? ' was' : 's were'} retained and do not need re-uploading.`
    : ' Nothing was retained — the whole batch needs re-uploading.';

const failedUploadDetail = (item: UploadActionItem): string => {
  const failed = failedUploadFiles(item);
  const first = failed[0];
  const total = fileCount(item, failed.length || 1);
  const progress = Number(first?.progress);
  const stopped =
    Number.isFinite(progress) && progress >= 0 && progress < 100
      ? ` stopped at ${Math.round(progress)}%`
      : '';
  const reason = first?.reason ? ` — ${first.reason}` : '';
  const kept = item.succeeded_files ?? (item.files || []).filter((file) => file.status === 'succeeded').length;
  return `${failed.length || 1} of ${total} files failed. ${first?.name || 'A file'}${stopped}${reason}.${retainedSentence(kept)}`;
};

const partialUploadDetail = (item: UploadActionItem): string => {
  const failed = failedUploadFiles(item)[0];
  const succeeded = item.succeeded_files ?? 0;
  const total = fileCount(item, succeeded + 1);
  const size = formatUploadSize(item.uploaded_bytes);
  const shown = rejectionCopy(failed?.reason);
  const reason = shown ? `: ${shown}` : '';
  return `${succeeded} of ${total} files uploaded${size ? ` · ${size}` : ''}. ${failed?.name || 'A file'} was rejected${reason}.`;
};

/** Title line: "Upload failed — UPL-2026-0184". */
export const uploadActionTitle = (item: UploadActionItem): string =>
  `${uploadPresentsAsFailure(item) ? 'Upload failed' : 'Upload incomplete'} — ${item.batch_ref}`;

/**
 * What failed, why, and what was retained (AC-3.3), in the Action Center wording.
 */
export const describeUploadActionItem = (item: UploadActionItem): string =>
  uploadPresentsAsFailure(item) ? failedUploadDetail(item) : partialUploadDetail(item);

export const uploadGroupBookingId = (item: UploadActionItem): string | null => {
  const fromFile = (item.files || []).map((file) => file.booking_group_id).find((id) => id);
  return fromFile ? String(fromFile) : null;
};

/** "Started 08:12 by Ibrahim Sait · failed 10:23 · 164.9 GB of 225.0 GB transferred". */
export const uploadActionMeta = (item: UploadActionItem): string => {
  const started = formatClock(item.started_at);
  const ended = formatClock(item.ended_at);
  const lead = [started ? `Started ${started}` : null, item.started_by_name ? `by ${item.started_by_name}` : null]
    .filter(Boolean)
    .join(' ');
  const failedCard = uploadPresentsAsFailure(item);
  const when = ended ? `${failedCard ? 'failed' : 'completed'} ${ended}` : null;
  const uploaded = formatUploadSize(item.uploaded_bytes);
  const total = formatUploadSize(item.total_bytes);
  const bytes = failedCard && uploaded && total ? `${uploaded} of ${total} transferred` : null;
  const group = failedCard ? null : uploadGroupBookingId(item);
  const against = group ? `against Group Booking ID ${group}` : null;
  return [lead, when, bytes, against].filter(Boolean).join(' · ');
};

/**
 * Primary action: Retry for a failure, Resolve for a partial (AC-3.4). The
 * wording differs because the work does: a failure is re-sent, while a partial
 * needs someone to decide what the rejected file should have been.
 */
export const uploadActionItemPrimaryLabel = (item: UploadActionItem): string => {
  if (!uploadPresentsAsFailure(item)) return 'Resolve';
  const name = failedUploadFileName(item);
  return name ? `Retry ${name}` : 'Retry';
};

/**
 * Secondary action opens the batch itself, not the project landing page.
 *
 * Data Uploads is a TAB inside /projects, not a route of its own — there is no
 * /data-uploads path, so linking to one 404s.
 */
export const buildUploadActionItemHref = (item: UploadActionItem): string =>
  `/projects?projectId=${encodeURIComponent(item.project_id)}&tab=data-uploads&upload=${encodeURIComponent(item.batch_ref)}`;

/** The file a Retry should name. The first failed file is the one still missing. */
export const failedUploadFileName = (item: UploadActionItem): string =>
  (item.files || []).find((file) => file.status === 'failed')?.name || '';

/**
 * Retry opens Data Uploads on the batch and names the failed file.
 * The item still clears only when that file is uploaded successfully.
 */
export const buildUploadRetryHref = (item: UploadActionItem): string => {
  const href = buildUploadActionItemHref(item);
  const fileName = failedUploadFileName(item);
  if (!fileName) return href;
  return `${href}&retryFile=${encodeURIComponent(fileName)}`;
};

/**
 * PM tab shows every item this viewer may see. Scheduler and Moderator tabs
 * show only items started in that role (AC-3.2).
 */
export const uploadActionsOnTab = (
  items: UploadActionItem[],
  role: 'pm' | 'scheduler' | 'moderator' | null,
): UploadActionItem[] => {
  if (role === 'pm') return items;
  if (!role) return [];
  return items.filter((item) => item.started_by_role === role);
};
